import classNames from 'classnames';
import type { InputHTMLAttributes } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import styles from './Switch.module.scss';

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  checked: boolean;
  onChangeChecked: (checked: boolean) => void;
  label?: string | React.ReactElement;
  size?: 'xsmall' | 'small' | 'medium';
  disabled?: boolean;
  className?: string;
}

const Switch = ({
  checked,
  onChangeChecked,
  label,
  size = 'medium',
  disabled,
  className = '',
  ...rest
}: SwitchProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasFocus, setHasFocus] = useState(false);
  const handleFocus = useCallback(() => setHasFocus(true), []);
  const handleBlur = useCallback(() => setHasFocus(false), []);
  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }
    const checkbox = inputRef.current;
    onChangeChecked(!checked);
    if (checkbox) {
      checkbox.focus();
    }
  }, [checked, disabled, onChangeChecked]);

  const classes = classNames({ [className]: !label }, styles.switch, styles[`size-${size}`], {
    [styles.checked]: checked,
    [styles.focus]: hasFocus,
    [styles.disabled]: disabled,
  });

  const switchArea = (
    <div className={classes} onClick={handleClick}>
      <div className={styles.track}>
        <div className={styles.trackInner}>
          <div className={styles.thumb} />
        </div>
      </div>

      <input
        {...rest}
        ref={inputRef}
        className={styles.screenreaderOnly}
        onFocus={handleFocus}
        onBlur={handleBlur}
        type="checkbox"
        disabled={disabled}
      />
    </div>
  );

  return label ? (
    <div
      className={classNames(className, styles.wrapper, styles[`size-${size}`], {
        [styles.checked]: checked,
        [styles.focus]: hasFocus,
        [styles.disabled]: disabled,
      })}
    >
      {switchArea}
      <div className={styles.label} onClick={handleClick}>
        {label}
      </div>
    </div>
  ) : (
    switchArea
  );
};

Switch.displayName = 'Switch';

export default Switch;
